// Legal links, per surface.
//
// Terms are published as PART I (common to everyone) plus ONE ANNEX per role. Which
// annexes a surface publishes follows from which roles it serves — the same split the
// backend enforces at sign-in — so interior.buildvizer.com does not offer an architect
// their annex, and app.buildvizer.com does not offer a buyer theirs.
//
// Mirrors the publication matrix in docs/legal/TERMS-OF-USE-DRAFT-v1.0.md, Part II.
// Keep the two in step: if a surface starts serving a new role, it must publish that
// role's annex, or people are asked to accept terms they were never shown.
//
// The Privacy Policy is a single document for everyone — it is not split by role.

import { APP_IDS, getAppId } from "./appIdentity";

export const LEGAL_BASE = "/legal";

/** Shown on every surface. */
export const SHARED_LEGAL_LINKS = [
  { label: "Privacy Policy", href: `${LEGAL_BASE}/privacy` },
  { label: "Terms of Use", href: `${LEGAL_BASE}/terms` },
  // Annex I applies to anyone reading a public page, signed in or not.
  { label: "Visitors & Public Pages", href: `${LEGAL_BASE}/terms/visitors` },
  // Not a legal document, but it belongs in the same footer: Play requires the deletion
  // route to be publicly reachable, and a link nobody can find is not reachable. This is
  // the URL declared in the Play Console under Data safety -> Data deletion.
  { label: "Delete Your Account", href: `${LEGAL_BASE}/delete-account` },
];

/** Role annexes, by surface.
 *
 * The Property Buyer (Annex A) and Channel Partner (Annex C) annexes are deliberately
 * absent: those roles sign in through the mobile apps, which link their own annex. Both
 * pages exist and are reachable by URL — nothing is unpublished, it is just not linked
 * from a footer belonging to a surface that does not serve that role.
 */
export const ROLE_LEGAL_LINKS = {
  [APP_IDS.WEB_APP]: [
    { label: "Developer Organisation Terms", href: `${LEGAL_BASE}/terms/developer` },
    { label: "Internal User Terms", href: `${LEGAL_BASE}/terms/internal` },
    // Vendors reach the public /tender/:token quote page on this surface, so their
    // annex has to be linked here — they never sign in and have no other route to it.
    { label: "Vendor / Supplier Terms", href: `${LEGAL_BASE}/terms/vendor` },
  ],
  [APP_IDS.WEB_INTERIOR]: [
    { label: "Interior Designer Terms", href: `${LEGAL_BASE}/terms/interior-designer` },
    { label: "Design Associate Terms", href: `${LEGAL_BASE}/terms/internal` },
  ],
  [APP_IDS.WEB_ARCH]: [
    { label: "Architect Terms", href: `${LEGAL_BASE}/terms/architect` },
    { label: "Independent Professional Terms", href: `${LEGAL_BASE}/terms/professional` },
  ],
};

/**
 * Role annexes for this surface.
 *
 * With no app id — local development, or a build whose env.js failed to generate —
 * returns every annex, so nothing is silently hidden while developing.
 */
export const roleLegalLinksForApp = (appId = getAppId()) => {
  if (!appId) return Object.values(ROLE_LEGAL_LINKS).flat();
  return ROLE_LEGAL_LINKS[appId] || [];
};
